import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, Clock } from 'lucide-react';
import { formatBRL } from '@/lib/format';
import { useAuthStore } from '@/features/auth/store';
import { useBillingSummary } from './api';


/**
 * Faixa fixa no topo do app avisando sobre a cobrança.
 *
 * Só aparece para admin: é quem pode resolver. E só quando há o que fazer —
 * primeiro pagamento pendente, fatura em atraso ou acesso suspenso. Em
 * operação normal (assinatura ativa) não ocupa espaço nenhum.
 */
export function BillingBanner() {
  const role = useAuthStore((s) => s.user?.role);
  const { pathname } = useLocation();
  const { data } = useBillingSummary({ enabled: role === 'admin' });

  if (role !== 'admin' || !data) return null;

  // Na própria tela de cobrança a faixa vira eco: a página já abre com o mesmo
  // aviso, em tamanho maior e com o botão certo. Duas vezes a mesma frase faz
  // o usuário reler procurando a diferença.
  if (pathname.startsWith('/app/billing')) return null;

  if (data.status === 'pending_payment') {
    // Fica visível o tempo todo, ao contrário dos outros: enquanto não pagar,
    // o sistema não opera, e esconder isso só gera a dúvida "por que não
    // consigo salvar nada?".
    return (
      <Banner tone="warning" icon={<Clock className="size-4 shrink-0" />}>
        {data.activeVehicles === 0
          ? 'Cadastre seus veículos para ver o valor e liberar o sistema.'
          : `${data.activeVehicles} veículo(s) × ${formatBRL(data.pricePerVehicleCents)} = ${formatBRL(data.projectedCents)}/mês.`}{' '}
        <BannerLink>
          {data.activeVehicles === 0 ? 'Ir para cobrança' : 'Pagar e liberar'}
        </BannerLink>
      </Banner>
    );
  }

  if (data.status === 'past_due') {
    return (
      <Banner tone="warning" icon={<AlertTriangle className="size-4 shrink-0" />}>
        Há fatura em aberto. O acesso será suspenso se o pagamento não for identificado.{' '}
        <BannerLink>Ver faturas</BannerLink>
      </Banner>
    );
  }

  if (data.status === 'suspended') {
    return (
      <Banner tone="destructive" icon={<AlertTriangle className="size-4 shrink-0" />}>
        Acesso suspenso por falta de pagamento. <BannerLink>Regularizar</BannerLink>
      </Banner>
    );
  }

  return null;
}

const TONES = {
  info: 'bg-primary/10 text-foreground border-primary/30',
  warning: 'bg-warning/10 text-foreground border-warning/40',
  destructive: 'bg-destructive/10 text-foreground border-destructive/40',
} as const;

function Banner({
  tone,
  icon,
  children,
}: {
  tone: keyof typeof TONES;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`flex items-center justify-center gap-2 border-b px-4 py-2 text-sm ${TONES[tone]}`}
      data-print-hide
    >
      {icon}
      <span>{children}</span>
    </div>
  );
}

function BannerLink({ children }: { children: React.ReactNode }) {
  return (
    <Link to="/app/billing" className="font-medium underline underline-offset-2">
      {children}
    </Link>
  );
}
